/**
 * DNACore V4 — Landing "O que há de novo"
 * Rota: /o-que-ha-de-novo
 * Também aparece como popup no primeiro login (via localStorage flag).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sparkles, Brain, ImagePlus, ClipboardCheck, Library, FileCheck2,
  Wand2, History, FileDown, Bookmark, Image as ImgIcon, MessageSquare, Layout,
  X, ArrowRight,
} from 'lucide-react';
import './WhatsNew.css';

export const WHATS_NEW_VERSION = 'v4-ai-assessment-studio-feb-2026';
export const WHATS_NEW_KEY = 'dnacore_whats_new_seen';

const FEATURES = [
  { icon: Sparkles, color: '#ec4899', title: 'Auto-Pilot 🚀',
    desc: 'Descreva a prova em 1 frase e a IA cria tudo: questões, gabarito, competências BNCC e exporta os 2 PDFs.' },
  { icon: Brain, color: '#6366f1', title: 'Assistente IA',
    desc: 'Painel lateral conversacional. Peça em linguagem natural: "Faça mais difícil", "Alinhe à BNCC", "ENEM".' },
  { icon: ImagePlus, color: '#f59e0b', title: 'Imagens com IA',
    desc: 'Gere ilustrações pedagógicas (mapas, gráficos, células, DNA) direto no editor via gpt-image-1.' },
  { icon: ImgIcon, color: '#f97316', title: 'Imagem contextual',
    desc: 'Selecione uma questão → clique "Imagem aqui" → IA analisa o enunciado e desenha automaticamente.' },
  { icon: ClipboardCheck, color: '#10b981', title: 'Revisão Inteligente',
    desc: 'Analisa BNCC, ortografia, tempo estimado, clareza, ambiguidade. Recebe nota geral e recomendações.' },
  { icon: Library, color: '#14b8a6', title: 'Banco de Questões',
    desc: 'Salve questões boas e reutilize em outras provas. Busca, favoritos e inserção com 1 clique.' },
  { icon: FileCheck2, color: '#0891b2', title: 'Notas Automáticas',
    desc: 'Upload da planilha de respostas dos alunos → notas calculadas automaticamente com o gabarito IA.' },
  { icon: Wand2, color: '#8E52AB', title: '14 ações IA por questão',
    desc: 'BubbleMenu com Refinar, Reescrever, Mais fácil/difícil, Pegadinhas, Trocar contexto, Similar, e mais.' },
  { icon: History, color: '#6366f1', title: 'Histórico IA + Desfazer',
    desc: 'Cada ação IA gera um snapshot. Reverta qualquer alteração com 1 clique. Contador semanal de uso.' },
  { icon: FileDown, color: '#4338ca', title: 'PDFs duais',
    desc: 'Gere em 1 clique o PDF do aluno (sem gabarito) e o PDF do professor (com gabarito comentado).' },
  { icon: Bookmark, color: '#f59e0b', title: 'Meus Modelos',
    desc: 'Salve uma avaliação ótima como modelo pessoal e crie novas provas a partir dela em 1 clique.' },
  { icon: MessageSquare, color: '#22c55e', title: 'Geração modular',
    desc: 'Gere isoladamente: gabarito comentado, competências BNCC, critérios de correção ou instruções.' },
  { icon: Layout, color: '#A574BF', title: 'Cabeçalho inteligente',
    desc: 'Cabeçalho institucional fixo. Os dados (disciplina, professor, valor) digitados na sidebar aparecem automaticamente.' },
];

export function markWhatsNewSeen() {
  try { localStorage.setItem(WHATS_NEW_KEY, WHATS_NEW_VERSION); } catch { /* noop */ }
}

export function hasSeenWhatsNew() {
  try { return localStorage.getItem(WHATS_NEW_KEY) === WHATS_NEW_VERSION; } catch { return false; }
}

/**
 * Auto-popup: monta o WhatsNew em modo embedded ao detectar primeiro acesso.
 * Inclui um delay de 1.5s para não atrapalhar o carregamento da app.
 */
export function WhatsNewAutoPopup() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const reportRoute = pathname.startsWith('/v2/academico/relatorios/') || pathname === '/relatorios' ||
    pathname.startsWith('/v2/academico/aluno') || pathname === '/v2/alunos' || pathname.startsWith('/v2/alunos/') ||
    pathname === '/v2/aluno' || pathname.startsWith('/v2/aluno/');
  useEffect(() => {
    // Não mostra em rotas públicas (landing pública, arena, checkin, etc.)
    const path = pathname;
    setOpen(false);
    const PUBLIC_PREFIXES = ['/conheca-o-novo-dna', '/novosdnas', '/arena', '/festa-junina/checkin', '/v2/festa-junina/checkin', '/login', '/notify', '/dna-sign/validar', '/familia', '/pedido-cadastro', '/enviodedocumentos', '/gestao', '/autorizacao-evento'];
    if (reportRoute || PUBLIC_PREFIXES.some((p) => path.startsWith(p))) return;
    if (hasSeenWhatsNew()) return;
    const t = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(t);
  }, [pathname, reportRoute]);
  if (!open || reportRoute) return null;
  // Gestão DNA tem chrome próprio e limpo — nunca exibe o popup
  if ((window.location.pathname || '').startsWith('/gestao')) return null;
  return <WhatsNew embedded onClose={() => setOpen(false)} />;
}

/**
 * Banner pequeno (para colocar em header/dashboard) que abre a landing.
 * Some quando o usuário já viu a versão atual.
 */
export function WhatsNewBanner() {
  const [show, setShow] = useState(!hasSeenWhatsNew());
  const navigate = useNavigate();
  if (!show) return null;
  return (
    <button
      className="whats-new-banner"
      onClick={() => { navigate('/o-que-ha-de-novo'); setShow(false); }}
      data-testid="whats-new-banner"
    >
      <Sparkles size={11} /> Editor de Avaliações atualizado · ver novidades
    </button>
  );
}

export default function WhatsNew({ embedded = false, onClose }) {
  const navigate = useNavigate();
  const close = () => {
    markWhatsNewSeen();
    if (onClose) onClose();
    else navigate('/');
  };
  const goToEditor = () => {
    markWhatsNewSeen();
    navigate('/v2/academico/editor-avaliacoes');
  };

  return (
    <div className={`whats-new-wrap ${embedded ? 'embedded' : ''}`} data-testid="whats-new">
      <div className="whats-new-card">
        {embedded && (
          <button className="whats-new-close" onClick={close} data-testid="whats-new-close"><X size={18} /></button>
        )}
        <div className="whats-new-hero">
          <div className="whats-new-badge">NOVO · DNACore V4</div>
          <h1>Seu Editor de Avaliações virou um <span>AI Assessment Studio</span></h1>
          <p>15 superpoderes movidos a IA — sem trocar de ferramenta, sem mocks, sem custo extra.</p>
          <div className="whats-new-cta">
            <button className="whats-new-btn-primary" onClick={goToEditor} data-testid="whats-new-cta">
              Abrir o Editor agora <ArrowRight size={16} />
            </button>
            <button className="whats-new-btn-ghost" onClick={close}>Ver depois</button>
          </div>
        </div>

        <div className="whats-new-grid" data-testid="whats-new-grid">
          {FEATURES.map((f, i) => {
            const Ico = f.icon;
            return (
              <div className="whats-new-feature" key={i}>
                <div className="whats-new-ico" style={{ background: `linear-gradient(135deg, ${f.color}, ${f.color}cc)` }}>
                  <Ico size={18} />
                </div>
                <div className="whats-new-feature-text">
                  <div className="t">{f.title}</div>
                  <div className="d">{f.desc}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="whats-new-footer">
          <div>
            <strong>Tudo isso já está disponível.</strong> Abra qualquer avaliação e procure os botões coloridos na toolbar.
          </div>
          <button className="whats-new-btn-primary" onClick={goToEditor}>
            Quero ver na prática <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
